/**
 * Schedule Visit Overlay Component
 */

import { useState } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Badge } from "../ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Calendar } from "lucide-react";
import { toast } from "sonner";

export function ScheduleVisitOverlay({ patientId, patientName, onSave, onClose }) {
  const [formData, setFormData] = useState({
    visit_type: "routine",
    discipline: "rn",
    date: "",
    time: "",
    duration: "60",
    priority: "normal",
    notes: "",
  });

  const handleSave = () => {
    if (!formData.date || !formData.time) {
      toast.error("Please select date and time");
      return;
    }
    toast.success("Visit scheduled successfully");
    if (onSave) onSave(formData);
    if (onClose) onClose();
  };

  return (
    <div className="space-y-4 p-4" data-testid="schedule-visit-overlay">
      <div className="flex items-center gap-2 mb-4">
        <Calendar className="w-5 h-5 text-teal-400" />
        <h3 className="font-semibold text-white">Schedule Visit</h3>
        <Badge variant="outline" className="border-slate-600 text-slate-300">{patientName}</Badge>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="text-slate-200">Visit Type</Label>
          <Select value={formData.visit_type} onValueChange={(v) => setFormData({...formData, visit_type: v})}>
            <SelectTrigger className="bg-slate-800 border-slate-700 text-white"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="routine">Routine Visit</SelectItem>
              <SelectItem value="prn">PRN Visit</SelectItem>
              <SelectItem value="admission">Admission Visit</SelectItem>
              <SelectItem value="recertification">Recertification</SelectItem>
              <SelectItem value="discharge">Discharge Visit</SelectItem>
              <SelectItem value="death_visit">Death Visit</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label className="text-slate-200">Discipline</Label>
          <Select value={formData.discipline} onValueChange={(v) => setFormData({...formData, discipline: v})}>
            <SelectTrigger className="bg-slate-800 border-slate-700 text-white"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="rn">RN</SelectItem>
              <SelectItem value="lpn">LPN</SelectItem>
              <SelectItem value="sw">Social Worker</SelectItem>
              <SelectItem value="chaplain">Chaplain</SelectItem>
              <SelectItem value="aide">Aide/CNA</SelectItem>
              <SelectItem value="pt">Physical Therapy</SelectItem>
              <SelectItem value="ot">Occupational Therapy</SelectItem>
              <SelectItem value="slp">Speech Therapy</SelectItem>
              <SelectItem value="md">Physician</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="text-slate-200">Date</Label>
          <Input 
            type="date" 
            value={formData.date} 
            onChange={(e) => setFormData({...formData, date: e.target.value})} 
            className="bg-slate-800 border-slate-700 text-white"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-slate-200">Time</Label>
          <Input 
            type="time" 
            value={formData.time} 
            onChange={(e) => setFormData({...formData, time: e.target.value})} 
            className="bg-slate-800 border-slate-700 text-white"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="text-slate-200">Duration (minutes)</Label>
          <Select value={formData.duration} onValueChange={(v) => setFormData({...formData, duration: v})}>
            <SelectTrigger className="bg-slate-800 border-slate-700 text-white"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="30">30 minutes</SelectItem>
              <SelectItem value="45">45 minutes</SelectItem>
              <SelectItem value="60">60 minutes</SelectItem>
              <SelectItem value="90">90 minutes</SelectItem>
              <SelectItem value="120">120 minutes</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label className="text-slate-200">Priority</Label>
          <Select value={formData.priority} onValueChange={(v) => setFormData({...formData, priority: v})}>
            <SelectTrigger className="bg-slate-800 border-slate-700 text-white"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="normal">Normal</SelectItem>
              <SelectItem value="urgent">Urgent</SelectItem>
              <SelectItem value="emergency">Emergency</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-2">
        <Label className="text-slate-200">Notes</Label>
        <Textarea 
          value={formData.notes} 
          onChange={(e) => setFormData({...formData, notes: e.target.value})} 
          placeholder="Any special instructions..." 
          rows={3} 
          className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
        />
      </div>

      <div className="flex justify-end gap-2 pt-4">
        <Button variant="outline" onClick={onClose} className="border-slate-600 text-slate-300 hover:bg-slate-800">Cancel</Button>
        <Button onClick={handleSave} className="bg-teal-600 hover:bg-teal-700"><Calendar className="w-4 h-4 mr-1" />Schedule</Button>
      </div>
    </div>
  );
}

export default ScheduleVisitOverlay;
